<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { PCheckbox } from '@profeat/ui-kit';

import { ContactField } from '~/entities/contacts';
import { useContacts } from '~/stores/contacts.store';

const props = defineProps<{
  fields: ContactField[];
  editableConfig: boolean;
}>();

defineEmits<{
  (event: 'remove-field', {fieldId, fieldGroup}: {fieldId: string, fieldGroup: 'system' | 'static' | 'custom'}): void;
  (event: 'add-field', {fieldId, fieldGroup}: {fieldId: string, fieldGroup: 'system' | 'static' | 'custom'}): void;
}>();

const contactsStore = useContacts();
const { t, te } = useI18n();

const fieldsMap = computed<Map<string, ContactField>>(() => {
  const result = new Map<string, ContactField>();
  props.fields.forEach((field) => {
    result.set(field.id, field);
  });
  return result;
});

const getFieldName = (field: ContactField) => {
  const path = `customers.header.${field.name}`;

  return te(path) ? t(path) : field.name;
};
const onChecked = (payload: boolean) => {
  contactsStore.selectionMode = payload ? 'all' : 'none';
};
</script>

<template>
  <tr>
    <th class="table-checkbox">
      <PCheckbox
        v-model="contactsStore.isAllSelected"
        @update:model-value="onChecked"
      />
    </th>

    <th v-for="field in fields" :key="field.id">
      <div class="header-cell">
        <span>{{ getFieldName(fieldsMap.get(field.id)!) }}</span>

        <CustomerTableFilter :field="fieldsMap.get(field.id)!" />
      </div>
    </th>

    <th class="table-settings">
      <CustomersTableSettings
        :editable="editableConfig"
        @add-field="$emit('add-field', $event)"
        @remove-field="$emit('remove-field', $event)"
      />
    </th>
  </tr>
</template>

<style lang="scss" scoped>
.sortable-chosen{
  opacity: 0.64;
  cursor: grabbing !important;
}
.sortable-ghost{
  cursor: grabbing !important;
  background:  #f1f5f9 !important;
}
th {
  text-align: left;

  .header-cell {
    display: flex;
    align-items: center;
    padding: 16px;
    grid-gap: 12px;
    // cursor: grab; //TODO при возврате dragAndDrop вернуть
    span {
      color: var(--common-black, #2b3238);
      font-size: 12px;
      font-weight: 600;
      line-height: 133.3%;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      max-width: 200px;
    }
  }

  .fields-list {
    display: flex;
    flex-direction: column;
    grid-gap: 12px;
    max-height: 460px;
    overflow-y: auto;

    :deep(label) {
      .label {
        white-space: nowrap;
      }
    }

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 25px;
      background: var(--common-rain, #d0dbe4);
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
}

.table-checkbox {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  padding: 16px 8px 16px 16px;
}

.table-settings {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px;
  position: sticky;
  left: calc(100% - 48px);
  right: 0;
  width: 48px;
  height: 48px;
  background: $ui-white;
  border-left: 1px solid var(--common-smog, #e5ebf0);
  border-bottom: 1px solid var(--common-smog, #e5ebf0);

  .icon {
    cursor: pointer;
    display: flex;
  }
}
</style>
